"use client";

import { fromDate, toCalendarDate } from "@internationalized/date";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useCalendar, useDateFormatter } from "react-aria";
import { sum } from "remeda";

import { TIMEZONE } from "@trip/constants";
import { cn } from "@trip/utils";

import { AngleDownIcon } from "~/components/icons/angle-down";
import { AngleLeftIcon } from "~/components/icons/angle-left";
import { AngleRightIcon } from "~/components/icons/angle-right";
import { CalendarIcon } from "~/components/icons/calendar-icon";
import { LoadingSpinner } from "~/components/loading-spinner";
import {
  PopoverStartTimesPicker,
  StartTimesPicker,
} from "~/components/start-times";
import { TourPassengerCategories } from "~/components/tour-passenger-categories";
import { SelectTourPlans, TourPlans } from "~/components/tour-plans";
import { Button, buttonVariants } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { type ProductId } from "~/lib/tours";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { useTourCalendar } from "~/lib/use-tour-calendar";
import { Link } from "~/navigation";
import { type RouterOutputs } from "~/trpc/types";

import { type TourFromPage } from "../page";
import { AddToBagButton } from "./add-to-bag-button";
import { Header } from "./header";
import { getPlanChoices } from "./lib";
import { RemoveCartLine } from "./remove-cart-line";
import { TourBookingCalendarGrid } from "./tour-booking-calendar";

type TourConfig = RouterOutputs["tours"]["config"];

export function TourBookingDesktop({
  datoTour,
  productId,
  config: initialData,
  inventory,
}: {
  datoTour: TourFromPage;
  productId: ProductId;
  config: TourConfig;
  inventory?: { amount: number; date: Date; perPerson: boolean };
}) {
  const locale = useLocale();
  const t = useTranslations("TourDetails");
  const tCheckout = useTranslations("Checkout");
  const formatCurrency = useCurrencyFormatter();
  const {
    calendarState,
    cartLine,
    choices,
    choice,
    dispatch,
    isFetching,
    startTime,
    startTimes,
    passengerCategories,
    config,
    totalPassengers,
    date,
    plan,
    errors,
  } = useTourCalendar({
    datoTourId: datoTour.id,
    productId,
    config: initialData,
    inventoryDate: inventory?.date,
    onChange: () => {
      setOpen(false);
    },
  });

  const [open, setOpen] = useState(false);

  const { calendarProps, title } = useCalendar({}, calendarState);

  const dateFormatter = useDateFormatter({
    dateStyle: "medium",
    timeZone: TIMEZONE,
  });

  // Dangerous useEffect - it forcefully moves from a null date to the first observed available date
  // in the calendar.
  useEffect(() => {
    const firstChoiceDate = choices[0]?.date;
    if (date === null && calendarState.value === null && firstChoiceDate) {
      calendarState.selectDate(
        toCalendarDate(fromDate(firstChoiceDate, "UTC")),
      );
      // Sorry this is SUPER hacky: Keep the popover open state, this overrides the onChange handler
      // switch above - if we don't do this then this effect will close the calendar without the
      // user interaction.
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
      setOpen(open);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sum(choices.map((choice) => choice.date.valueOf())), isFetching]);

  const planChoices = getPlanChoices({
    datoPlans: datoTour.plans,
    formatDate: (date: Date) => dateFormatter.format(date),
    plans: config.plans,
    t,
  });

  return (
    <>
      <>
        <Header
          amount={
            config.pricedPerPassenger
              ? inventory?.amount
              : choices.find(({ amount }) => amount)?.amount
          }
          tour={datoTour}
          pricedPerPassenger={config.pricedPerPassenger}
        />
        <div className="mt-4 flex flex-col gap-3">
          {config.plans.length > 3 ? (
            <SelectTourPlans
              choices={planChoices}
              plan={plan}
              setPlanId={(id) => {
                dispatch({ type: "update-plan", id });
              }}
            />
          ) : config.plans.length > 1 ? (
            <TourPlans
              plan={plan}
              choices={planChoices}
              setPlanId={(id) => {
                dispatch({ type: "update-plan", id });
              }}
            />
          ) : null}

          {config.plan.openDate ? null : (
            <Popover
              onOpenChange={(open) => {
                setOpen(open);
              }}
              open={open}
            >
              <PopoverTrigger asChild>
                <Button
                  className="w-full justify-between"
                  aria-label={t("whensYourTrip")}
                  disabled={Boolean(cartLine)}
                >
                  <div className="flex items-center gap-2">
                    <CalendarIcon />
                    {calendarState.value
                      ? dateFormatter.format(
                          calendarState.value.toDate(TIMEZONE),
                        )
                      : null}
                  </div>
                  <AngleDownIcon />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="shadow-card-search w-auto rounded-2xl border-none p-3"
                sideOffset={8}
              >
                <div
                  className="flex min-w-64 flex-col items-stretch gap-2 *:w-full"
                  {...calendarProps}
                >
                  <div className="flex items-center">
                    <Button
                      onClick={() => {
                        calendarState.focusPreviousPage();
                      }}
                      size="circle"
                      variant="ghost"
                    >
                      <AngleLeftIcon />
                    </Button>
                    <div className="grow text-center text-sm font-medium">
                      {title}
                    </div>
                    <Button
                      onClick={() => {
                        calendarState.focusNextPage();
                      }}
                      size="circle"
                      variant="ghost"
                    >
                      <AngleRightIcon />
                    </Button>
                  </div>
                  <TourBookingCalendarGrid
                    isLoading={isFetching}
                    dates={choices.map(({ date }) =>
                      toCalendarDate(fromDate(date, "UTC")),
                    )}
                    locale={locale}
                    state={calendarState}
                  />
                </div>
              </PopoverContent>
            </Popover>
          )}

          {startTimes.length > 3 ? (
            <PopoverStartTimesPicker
              key={`${date?.toString()}-${isFetching}`}
              value={startTime?.id ?? null}
              onChange={(value) => {
                const startTime = startTimes.find(({ id }) => value === id);
                if (startTime) {
                  dispatch({
                    type: "update-start-time",
                    starTime: {
                      hour: startTime.hour ?? 0,
                      minute: startTime.minute ?? 0,
                    },
                  });
                }
              }}
              startTimes={startTimes}
              disabled={Boolean(cartLine)}
            />
          ) : startTimes.length > 0 ? (
            <StartTimesPicker
              value={startTime?.id ?? null}
              onChange={(value) => {
                const startTime = startTimes.find(({ id }) => value === id);
                if (startTime) {
                  dispatch({
                    type: "update-start-time",
                    starTime: {
                      hour: startTime.hour ?? 0,
                      minute: startTime.minute ?? 0,
                    },
                  });
                }
              }}
              startTimes={startTimes}
            />
          ) : null}

          <hr className="h-px bg-neutral-900/[0.08]" />

          <div className="flex flex-col gap-4">
            <TourPassengerCategories
              onChange={({ id, count }) => {
                dispatch({
                  type: "update-passenger-count",
                  config,
                  count,
                  id,
                });
              }}
              passengerCategories={passengerCategories}
              platform={config.platform}
              disabled={Boolean(cartLine)}
            />
          </div>

          <hr className="h-px bg-neutral-900/[0.08]" />

          <div className="relative min-h-6 w-full text-center text-sm text-neutral-500">
            {cartLine ? (
              <RemoveCartLine cartLine={cartLine} />
            ) : choice ? (
              <>
                {t("personsForAmount", {
                  count: totalPassengers,
                  amount: formatCurrency(choice.amount),
                })}
              </>
            ) : isFetching ? (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <LoadingSpinner />
              </div>
            ) : (
              t("noAvailabilityFound")
            )}
          </div>

          {cartLine ? (
            <Link
              className={cn(
                buttonVariants({
                  size: "lg",
                  variant: "outline",
                }),
                "w-full rounded-full",
              )}
              href="/checkout"
            >
              {tCheckout("continueToCheckout")}
            </Link>
          ) : (
            <AddToBagButton
              loading={isFetching}
              disabled={errors.length > 0 || !choice}
              choice={choice!}
              datoTour={datoTour}
              passengerCategories={passengerCategories}
              plan={plan}
              productId={productId}
              startTime={startTime}
              totalPassengers={totalPassengers}
            />
          )}

          {errors.length ? (
            <div className="bg-destructive/10 text-destructive rounded-xl px-3 py-1.5 text-center text-sm">
              {errors.map((error, index) => (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <div key={index + error}>{error}</div>
              ))}
            </div>
          ) : null}
        </div>
      </>
      {config.cancellationPolicy.length > 0 ? (
        <div className="mt-4 grid gap-1 text-center text-xs text-neutral-500">
          {config.cancellationPolicy.map((policy, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <div key={index}>{policy}</div>
          ))}
        </div>
      ) : null}
    </>
  );
}
